/** @jsxFrag React.Fragment */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import ScrollAnchor from 'bundles/common/components/ScrollAnchor';
import DegreeFilters from 'bundles/premium-hub/components/degrees/DegreeFilters';
import TopDegreesList from 'bundles/premium-hub/components/degrees/TopDegreesList';
import type { DegreeFilterState } from 'bundles/premium-hub/components/degrees/useMultiSelectDegreeFilterStateManager';
import Container from 'bundles/premium-hub/components/shared/Container';
import { type PremiumHubPageType } from 'bundles/premium-hub/constants';
import { usePremiumHubExperimentValues } from 'bundles/premium-hub/contexts/PremiumHubPageDataContext';
import type { Domain, PremiumProductWithMetadata } from 'bundles/premium-hub/types';

const DegreeFilterChipDropdowns = React.lazy(
  () => import('bundles/premium-hub/components/degrees/DegreeFilterChipDropdowns')
);

type Props = {
  degreeFilterState: DegreeFilterState;
  slug: string;
  categoryName?: string;
  currentPageType?: PremiumHubPageType;
  domains: Domain[];
  filteredDegreeList: Array<PremiumProductWithMetadata>;
  degreeListWithMetadata?: PremiumProductWithMetadata[];
};

const styles = {
  wrapper: css`
    margin-top: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-150);
    }
  `,
  degreesListWrapper: css`
    ${breakpoints.down('sm')} {
      padding: 0 var(--cds-spacing-150);
    }
  `,
};

const TopDegreeListSection = ({
  degreeFilterState,
  slug,
  categoryName,
  currentPageType,
  domains,
  filteredDegreeList,
  degreeListWithMetadata,
}: Props) => {
  const { degreeListRefresh } = usePremiumHubExperimentValues();

  if (!degreeListWithMetadata?.length) {
    return null;
  }

  return (
    <div css={styles.wrapper}>
      <Container>
        {degreeListRefresh ? (
          <ErrorBoundaryWithTagging componentName="DegreeFilterChipDropdowns">
            <React.Suspense fallback={null}>
              <DegreeFilterChipDropdowns
                degreeFilterState={degreeFilterState}
                slug={slug ?? ''}
                categoryName={categoryName}
                currentPageType={currentPageType}
                degrees={filteredDegreeList ?? []}
              />
            </React.Suspense>
          </ErrorBoundaryWithTagging>
        ) : (
          <DegreeFilters
            degreeFilterState={degreeFilterState}
            slug={slug ?? ''}
            productId="degrees"
            surveyProductType="GenericPage"
            categoryName={categoryName}
            currentPageType={currentPageType}
            domains={domains ?? []}
            degrees={filteredDegreeList ?? []}
          />
        )}
        <ScrollAnchor />
        <div css={degreeListRefresh ? null : styles.degreesListWrapper}>
          <TopDegreesList degrees={filteredDegreeList ?? []} />
        </div>
      </Container>
    </div>
  );
};

export default React.memo(TopDegreeListSection);
